import { events } from "@constants/tracking";
import { getDescAndCTA, getMappedExams } from "@helpers/exams";
import type {
	AppConfigMasteryLevelsType,
	SessionConfigFiltersType,
} from "@memorang/constants/config/schema";
import { convertToMinutes } from "@memorang/helpers/common";
import { formatList } from "@memorang/helpers/i18n";
import type {
	ExamStatus,
	MixedItemTag,
	PracticeTestExam,
} from "@memorang/types";
import type {
	TaskChip,
	TasksSectionItem,
} from "@memorang/ui/components/HomeApplet/types";
import { getSkillIcon } from "@memorang/ui/constants";
import { trackCustomEvent } from "analytics";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import {
	buildStructuredTagSelection,
	computeStructuredTagsDist,
	getQuestionTypesForSkill,
} from "../../../session-config/helpers/session-filters";
import type {
	QuickStudyMeta,
	RecommendedSkillPick,
	RecommendedSkillTask,
} from "../../recommendations/adapters/dashboard-task-types";
import { capLaunchDistribution } from "../../recommendations/adapters/launch-distribution";

const DEFAULT_STRONG_SKILL_THRESHOLD = 75;
const DEFAULT_MASTERED_SKILL_THRESHOLD = 90;
const DEFAULT_WEAK_SKILL_THRESHOLD = 50;

export const PRACTICE_TEST_COOLDOWN_DAYS = 7;
export const SECTIONAL_TEST_COOLDOWN_DAYS = 3;

const RECENT_ACTIVE_WINDOW_MS = 24 * 60 * 60 * 1000;
const RECENT_COMPLETED_WINDOW_MS = 48 * 60 * 60 * 1000;

const sortByCreatedAtDesc = (a: PracticeTestExam, b: PracticeTestExam) =>
	new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

const getAttemptedCount = (tag: MixedItemTag): number =>
	Object.entries(tag.distribution).reduce((total, [key, value]) => {
		if (key === "none") {
			return total;
		}
		return total + (value ?? 0);
	}, 0);

export const getSkillPerformanceScore = (skill: MixedItemTag): number => {
	const none = skill.distribution.none ?? 0;
	const low = skill.distribution.low ?? 0;
	const medium = skill.distribution.medium ?? 0;
	const high = skill.distribution.high ?? 0;
	const total = none + low + medium + high;

	if (total === 0) {
		return 0;
	}

	return Math.round(((medium * 0.5 + high) / total) * 100);
};

export const isSkillPerformanceStrong = (
	skill: MixedItemTag,
	strongThreshold = DEFAULT_STRONG_SKILL_THRESHOLD,
): boolean => getSkillPerformanceScore(skill) >= strongThreshold;

export const isSkillMastered = (
	skill: MixedItemTag,
	masteredThreshold = DEFAULT_MASTERED_SKILL_THRESHOLD,
): boolean => getSkillPerformanceScore(skill) >= masteredThreshold;

export { filterFullPracticeTests, filterSectionalTests } from "./test-kinds";

export const getRecentActiveTest = (
	allTests: PracticeTestExam[],
	now = Date.now(),
): PracticeTestExam | undefined =>
	allTests
		.filter((item) => item.status === "ACTIVE")
		.sort(sortByCreatedAtDesc)
		.find(
			(item) =>
				new Date(item.createdAt).getTime() > now - RECENT_ACTIVE_WINDOW_MS,
		);

export const getRecentCompletedTest = (
	allTests: PracticeTestExam[],
	now = Date.now(),
): PracticeTestExam | undefined =>
	allTests
		.filter((item) => item.status === "COMPLETE")
		.sort(sortByCreatedAtDesc)
		.find(
			(item) =>
				new Date(item.dateCompleted).getTime() >
				now - RECENT_COMPLETED_WINDOW_MS,
		);

export const getNextEligibleTest = ({
	tests,
	cooldownDays,
}: {
	tests: PracticeTestExam[];
	cooldownDays: number;
}): PracticeTestExam | undefined => {
	const nextTest = tests.find((item) => item.status === "NOT_STARTED");
	if (!nextTest) {
		return undefined;
	}

	const lastCompletedTest = tests
		.filter((item) => item.status === "COMPLETE")
		.sort(sortByCreatedAtDesc)[0];

	if (!lastCompletedTest) {
		return nextTest;
	}

	const daysSinceLast = DateTime.fromISO(nextTest.createdAt).diff(
		DateTime.fromISO(lastCompletedTest.createdAt),
		"days",
	).days;

	return daysSinceLast > cooldownDays ? nextTest : undefined;
};

const getSkillsToEvaluate = (
	skills: MixedItemTag[],
	preferredSkillIds?: string[],
): MixedItemTag[] => {
	if (!skills.length) {
		return [];
	}

	return preferredSkillIds?.length
		? skills.filter((skill) =>
				preferredSkillIds.includes(skill.title.toLowerCase()),
			)
		: [skills[0]];
};

const skillMatchesPreferredId = (
	skill: MixedItemTag,
	preferredId: string,
): boolean => {
	const needle = preferredId.toLowerCase();
	return (
		skill.title.toLowerCase() === needle ||
		(skill.code ? skill.code.toLowerCase() === needle : false) ||
		String(skill.id) === preferredId
	);
};

const orderSkillsByPreference = (
	skills: MixedItemTag[],
	preferredSkillIds?: string[],
): MixedItemTag[] => {
	if (!preferredSkillIds?.length) {
		return skills;
	}
	const remaining = [...skills];
	const preferred: MixedItemTag[] = [];
	for (const preferredId of preferredSkillIds) {
		const index = remaining.findIndex((skill) =>
			skillMatchesPreferredId(skill, preferredId),
		);
		if (index >= 0) {
			const [matched] = remaining.splice(index, 1);
			if (matched) {
				preferred.push(matched);
			}
		}
	}
	return [...preferred, ...remaining];
};

export const pickRecommendedDiagnostics = ({
	diagnostics,
	skills,
	preferredSkillIds,
}: {
	diagnostics: PracticeTestExam[];
	skills: MixedItemTag[];
	preferredSkillIds?: string[];
}): PracticeTestExam[] => {
	const incompleteDiagnostics = diagnostics.filter(
		(diagnostic) => diagnostic.status !== "COMPLETE",
	);

	if (!incompleteDiagnostics.length) {
		return [];
	}

	const skillsToEvaluate = getSkillsToEvaluate(skills, preferredSkillIds);
	const skillNames = skillsToEvaluate.length
		? skillsToEvaluate.map((skill) => skill.title.toLowerCase())
		: preferredSkillIds?.length
			? preferredSkillIds
			: [];

	if (!skillNames.length) {
		return incompleteDiagnostics;
	}

	const preferred: PracticeTestExam[] = [];
	const preferredIds = new Set<string>();

	for (const skillName of skillNames) {
		const match = incompleteDiagnostics.find(
			(diagnostic) =>
				!preferredIds.has(diagnostic.id) &&
				diagnostic.title.toLowerCase().includes(skillName),
		);
		if (match) {
			preferredIds.add(match.id);
			preferred.push(match);
		}
	}

	const rest = incompleteDiagnostics.filter(
		(diagnostic) => !preferredIds.has(diagnostic.id),
	);

	return [...preferred, ...rest];
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: intentionally flat recommendation decision tree
export function pickRecommendedSkills({
	skills,
	proficiencies,
	questionTypes,
	weakThreshold = DEFAULT_WEAK_SKILL_THRESHOLD,
	strongThreshold = DEFAULT_STRONG_SKILL_THRESHOLD,
	proficiencyLevels,
	preferredSkillIds,
	currentProficiencyLevel,
}: {
	skills: MixedItemTag[] | undefined;
	proficiencies: MixedItemTag[] | undefined;
	questionTypes: MixedItemTag[] | undefined;
	weakThreshold?: number;
	strongThreshold?: number;
	proficiencyLevels?: AppConfigMasteryLevelsType;
	preferredSkillIds?: string[];
	currentProficiencyLevel?: string;
}) {
	if (!skills?.length) {
		return [];
	}

	const skillsToEvaluate = orderSkillsByPreference(skills, preferredSkillIds);

	const orderedProficiencies = proficiencies?.length
		? [...proficiencies].sort((a, b) => {
				if (!proficiencyLevels?.length) {
					return 0;
				}
				const aSortOrder = proficiencyLevels.find(
					(level) => level.id === a.title.toLowerCase(),
				)?.sortOrder;
				const bSortOrder = proficiencyLevels.find(
					(level) => level.id === b.title.toLowerCase(),
				)?.sortOrder;
				return (aSortOrder ?? 999) - (bSortOrder ?? 999);
			})
		: [];

	const preferredProficiency =
		(currentProficiencyLevel
			? orderedProficiencies.find(
					(proficiency) =>
						proficiency.title.toLowerCase() ===
						currentProficiencyLevel.toLowerCase(),
				)
			: undefined) ?? orderedProficiencies[0];

	const picks: RecommendedSkillPick[] = [];
	for (const skill of skillsToEvaluate) {
		const skillScore = getSkillPerformanceScore(skill);
		const skillOnlyKind =
			skillScore < strongThreshold ? "weakSkill" : "strongSkill";

		const skillQuestionTypes = getQuestionTypesForSkill(
			questionTypes ?? [],
			skill,
		);
		if (skillQuestionTypes.length === 0) {
			picks.push({ skill, kind: skillOnlyKind });
			continue;
		}

		let currentQuestionType = skillQuestionTypes[0];
		let currentAttemptCount = getAttemptedCount(currentQuestionType);
		for (const questionType of skillQuestionTypes) {
			const attemptCount = getAttemptedCount(questionType);
			if (attemptCount > currentAttemptCount) {
				currentQuestionType = questionType;
				currentAttemptCount = attemptCount;
			}
		}

		let otherQuestionType: MixedItemTag | undefined;
		let otherAttemptCount = -1;
		for (const questionType of skillQuestionTypes) {
			if (questionType.id === currentQuestionType.id) {
				continue;
			}
			const attemptCount = getAttemptedCount(questionType);
			if (!otherQuestionType || attemptCount > otherAttemptCount) {
				otherQuestionType = questionType;
				otherAttemptCount = attemptCount;
			}
		}

		if (skillScore < strongThreshold) {
			picks.push({
				skill,
				kind: "weakSkill",
				questionTypes: [currentQuestionType],
				proficiency: preferredProficiency,
			});
			continue;
		}

		const questionTypeScore = getSkillPerformanceScore(currentQuestionType);
		if (questionTypeScore >= strongThreshold) {
			const currentProficiencyIndex = preferredProficiency
				? orderedProficiencies.findIndex(
						(proficiency) => proficiency.id === preferredProficiency.id,
					)
				: -1;
			const nextProficiency =
				currentProficiencyIndex >= 0
					? orderedProficiencies[currentProficiencyIndex + 1]
					: undefined;
			picks.push({
				skill,
				kind: nextProficiency ? "higherDifficulty" : "strongSkill",
				questionTypes: [currentQuestionType],
				proficiency: nextProficiency ?? preferredProficiency,
			});
			continue;
		}

		if (questionTypeScore >= weakThreshold) {
			picks.push({
				skill,
				kind: "mixedSkill",
				questionTypes: otherQuestionType
					? [currentQuestionType, otherQuestionType]
					: [currentQuestionType],
				proficiency: preferredProficiency,
			});
			continue;
		}

		picks.push({
			skill,
			kind: "weakSkill",
			questionTypes: [currentQuestionType],
			proficiency: preferredProficiency,
		});
	}

	return picks;
}

type HandlePracticeTestSession = (
	examId: string,
	status: ExamStatus,
	numericId?: number,
	isQuiz?: boolean,
	hasExamAccess?: boolean,
	replace?: boolean,
	collectionId?: number,
	shouldStartSession?: boolean,
	isRecommendedTask?: boolean,
) => void;

type BuildTestItemParams = {
	test: PracticeTestExam;
	status: Extract<ExamStatus, "ACTIVE" | "COMPLETE" | "NOT_STARTED">;
	body: string;
	t: TFunction;
	handlePracticeTestSession: HandlePracticeTestSession;
	selectedExamId?: string;
	creatingSession?: boolean;
};

const buildTestChips = (
	test: PracticeTestExam,
	status: BuildTestItemParams["status"],
	t: TFunction,
): TaskChip[] => {
	const chips: TaskChip[] = [
		{
			icon: "card-multiple-outline",
			label: t("dashboard.recommendedTasks.itemsChip", {
				count: test.numItems,
			}),
		},
	];

	if (status === "ACTIVE") {
		const minutes = convertToMinutes(test.totalTime - test.timeElapsed);
		chips.push({
			icon: "clock-outline",
			label: t("dashboard.recommendedTasks.timeRemainingChip", { minutes }),
			compactLabel: t("dashboard.recommendedTasks.timeRemainingChipCompact", {
				minutes,
			}),
		});
	} else if (status === "NOT_STARTED") {
		chips.push({
			icon: "clock-outline",
			label: t("sessionConfig.quickStudy.duration", {
				minutes: convertToMinutes(test.totalTime),
			}),
		});
	}

	return chips;
};

export const buildTestItem = ({
	test,
	status,
	body,
	t,
	handlePracticeTestSession,
	selectedExamId,
	creatingSession,
}: BuildTestItemParams): TasksSectionItem => {
	const mapped = getMappedExams([test])[0];
	const isQuiz = mapped.subType === "DIAGNOSTIC";
	return {
		title: test.title,
		subtitle: getDescAndCTA(test).description,
		status,
		body,
		chips: buildTestChips(test, status, t),
		kind: "test",
		icon: mapped.completionRing?.icon,
		completion:
			mapped.completionRing?.progress ?? (status === "COMPLETE" ? 100 : 0),
		loading: creatingSession && selectedExamId === mapped.id,
		onPress: () => {
			trackCustomEvent({
				eventName: events.taskCardClicked,
				isDiagnostic: `${isQuiz}`,
				status: mapped.status,
				numericId: mapped.numericId,
				hasAccess: `${mapped.hasExamAccess}`,
			});
			handlePracticeTestSession(
				mapped.id,
				mapped.status,
				mapped.numericId,
				isQuiz,
				mapped.hasExamAccess,
				false,
				mapped.collectionId,
				false,
				true,
			);
		},
	};
};

export const buildSkillChips = (
	quickStudyMeta: QuickStudyMeta | undefined,
	t: TFunction,
): TaskChip[] | undefined => {
	if (!quickStudyMeta) {
		return undefined;
	}

	const { numItems, minutes } = quickStudyMeta;
	return [
		{
			icon: "card-multiple-outline",
			label: t("dashboard.recommendedTasks.itemsChip", { count: numItems }),
		},
		{
			icon: "clock-outline",
			label: t("sessionConfig.quickStudy.duration", { minutes }),
		},
	];
};

type BuildRecommendedSkillTasksParams = {
	skills: MixedItemTag[];
	proficiencies: MixedItemTag[];
	questionTypes: MixedItemTag[];
	proficiencyLevels?: AppConfigMasteryLevelsType;
	preferredSkillIds?: string[];
	currentProficiencyLevel?: string;
	distributionFilterItems?: SessionConfigFiltersType;
	crossTagSelection?: boolean;
	t: TFunction;
	onSelectTags?: (tagIds: string[]) => void;
};

export const buildRecommendedSkillTaskTitle = (
	recommendation: RecommendedSkillPick,
	t: TFunction,
): string => {
	switch (recommendation.kind) {
		case "weakSkill":
			return t("dashboard.recommendedTasks.weakSkillTitle", {
				skill: recommendation.skill.title,
			});
		case "strongSkill":
			return t("dashboard.recommendedTasks.strongSkillTitle", {
				skill: recommendation.skill.title,
			});
		case "mixedSkill":
			return t("dashboard.recommendedTasks.mixedSkillTitle", {
				skill: recommendation.skill.title,
			});
		case "higherDifficulty":
			return t("dashboard.recommendedTasks.higherDifficultySkillTitle", {
				proficiency: recommendation.proficiency?.title,
			});
		default:
			return recommendation.skill.title;
	}
};

export const buildRecommendedSkillTaskBody = (
	recommendation: RecommendedSkillPick,
	t: TFunction,
): string => {
	if (!recommendation.questionTypes?.length) {
		return "";
	}

	const questionType = recommendation.questionTypes[0].title;

	switch (recommendation.kind) {
		case "weakSkill":
			return t("dashboard.recommendedTasks.weakSkillBody", {
				questionType,
			});
		case "strongSkill":
			return t("dashboard.recommendedTasks.strongSkillBody", {
				questionType,
			});
		case "mixedSkill":
			return t("dashboard.recommendedTasks.mixedSkillBody", {
				questionTypes: formatList(
					undefined,
					recommendation.questionTypes.map(
						(questionType) => questionType.title,
					),
				),
			});
		case "higherDifficulty":
			return t("dashboard.recommendedTasks.higherDifficultySkillBody", {
				questionType,
			});
		default:
			return "";
	}
};

const getRecommendedSkillTaskTagIds = ({
	proficiency,
	questionTypes,
	skill,
}: RecommendedSkillPick): string[] => {
	const tagIds = [String(skill.id)];

	if (proficiency?.id) {
		tagIds.push(String(proficiency.id));
	}

	if (questionTypes?.length) {
		tagIds.push(
			...questionTypes.map((questionType) => String(questionType.id)),
		);
	}

	return tagIds;
};

const QUICK_STUDY_TIME_PER_QUESTION = 72;
const QUICK_STUDY_MAX_ITEMS = 50;

const groupRecommendationTagIds = (
	recommendation: RecommendedSkillPick,
): number[][] =>
	[
		[Number(recommendation.skill.id)],
		recommendation.proficiency
			? [Number(recommendation.proficiency.id)]
			: undefined,
		recommendation.questionTypes?.length
			? recommendation.questionTypes.map((questionType) =>
					Number(questionType.id),
				)
			: undefined,
	].filter((group): group is number[] => !!group);

const buildQuickStudyMeta = ({
	topLevelTags,
	recommendation,
	tagIds,
	proficiencyLevels,
	distributionFilterItems,
	crossTagSelection,
}: {
	topLevelTags: MixedItemTag[];
	recommendation: RecommendedSkillPick;
	tagIds: string[];
	proficiencyLevels?: AppConfigMasteryLevelsType;
	distributionFilterItems?: SessionConfigFiltersType;
	crossTagSelection?: boolean;
}): QuickStudyMeta | undefined => {
	if (!distributionFilterItems) {
		return undefined;
	}

	const status = buildStructuredTagSelection(
		topLevelTags,
		tagIds.map(Number),
		proficiencyLevels,
	);
	// Preserves the pre-existing "no matching question types = no recommendation"
	// behavior: a skill's question-type/proficiency facet with zero checked tags
	// counts as zero items here, not "no filter" (the Quick Study modal's
	// empty-filter semantics are out of scope for this dashboard path).
	const rawDistribution = computeStructuredTagsDist(
		topLevelTags,
		status,
		distributionFilterItems,
		false,
	);
	const numItems = Math.min(
		Object.values(rawDistribution).reduce(
			(acc, value) => acc + (value ?? 0),
			0,
		),
		QUICK_STUDY_MAX_ITEMS,
	);

	if (numItems === 0) {
		return undefined;
	}

	const distribution = capLaunchDistribution(rawDistribution, numItems);
	const groupedTags = groupRecommendationTagIds(recommendation);
	const tags =
		crossTagSelection || groupedTags.length > 1
			? groupedTags
			: [tagIds.map(Number)];

	return {
		numItems,
		minutes: convertToMinutes(numItems * QUICK_STUDY_TIME_PER_QUESTION),
		distribution,
		tags,
	};
};

export const buildRecommendedSkillTasks = ({
	skills,
	proficiencies,
	questionTypes,
	proficiencyLevels,
	preferredSkillIds,
	currentProficiencyLevel,
	distributionFilterItems,
	crossTagSelection,
	t,
	onSelectTags,
}: BuildRecommendedSkillTasksParams): RecommendedSkillTask[] =>
	pickRecommendedSkills({
		skills,
		proficiencies,
		questionTypes,
		proficiencyLevels,
		preferredSkillIds,
		currentProficiencyLevel,
	}).map((recommendation) => {
		const { skill } = recommendation;
		const tagIds = getRecommendedSkillTaskTagIds(recommendation);
		const quickStudyMeta = buildQuickStudyMeta({
			topLevelTags: [...skills, ...proficiencies, ...questionTypes],
			recommendation,
			tagIds,
			proficiencyLevels,
			distributionFilterItems,
			crossTagSelection,
		});

		return {
			title: buildRecommendedSkillTaskTitle(recommendation, t),
			status: "NOT_STARTED",
			body: buildRecommendedSkillTaskBody(recommendation, t),
			chips: buildSkillChips(quickStudyMeta, t),
			kind: "session",
			icon: getSkillIcon(skill.title),
			tagIds,
			recommendation,
			quickStudyMeta,
			onPress: () => onSelectTags?.(tagIds),
		};
	});
